'use strict';

import {publish} from '../helpers/pubSub';
import * as dialog from '../components/dialog';
import * as progress from '../components/progress';
import {processAjaxForm} from './module.ajax-form';
import loadGoogleMapsAPI from 'load-google-maps-api';
import {populateAddressSummary} from '../checkout/summaryHelpers';
import {addEventListenerList, dispatchEvent, objectAssign, initModules} from '../helpers/utilities';
import {callbackOnElements, createElWithAttrs} from '@borngroup/born-utilities';
import {appendParamsToURL, getQueryStringParams} from '../helpers/urlUtilities';


//U.S. Map in the center of the viewport
const
    INITIAL_LAT = 37.09024,
    INITIAL_LNG = -95.712891,
    storeIDInputName = 'storeId',
    APIConfig = {
        key: window.SFRA.SitePreferences.mapsAPIKey,
    };

export function init(elements, options = {}) {
    loadGoogleMapsAPI(APIConfig).then(function(mapsAPI) {
        options.mapsAPI = mapsAPI;

        callbackOnElements(elements, _setupLocator.bind(this, options));
    });
}

/**
 * Renders the results of the search and updates the map
 * @param {Object} data - Response from the server
 */
export function updateStoresResults(response, payload, locatorSettings) {
    let resultsEl = locatorSettings.locatorEl.querySelector('[data-locator-component="result-list"]'),
        hasResults = response.stores.length;

    _storeSelectionHandler(locatorSettings);

    _toggleNoResults(!hasResults);

    resultsEl.textContent = '';

    _renderMap(response, payload, locatorSettings);

    if (response.storesResultsHtml) {
        resultsEl.insertAdjacentHTML('afterbegin', response.storesResultsHtml);

        _storeSelectionListener(locatorSettings);
    }

    initModules(resultsEl);

    publish('storeLocator:updateRendered', {response, payload, locatorSettings});

    // Override for Denali
    if (window.SFRA.Denali) {
        let boutiqueSelect = locatorSettings.locatorEl.querySelector('[data-denali-boutique-select]');

        boutiqueSelect.options.length = 1;

        [].forEach.call(response.stores, function(store) {
            boutiqueSelect.add(new Option(store.name, store.ID));
        });

        boutiqueSelect.focus();
        locatorSettings.locatorEl.querySelector('[data-denali-boutique-action]').classList.add('disabled');
        locatorSettings.locatorEl.querySelector('[data-focusout-element]').focus();
    }
}

/**
 * Sends a request to check if there are any stores within the specified search query + radius.
 */
export function requestStoreSearch(url, payload, locatorSettings) {
    if (_shouldRequestSearch(locatorSettings)) {
        progress.showLoader();

        return $.ajax({
            url: url || window.SFRA.Urls.storeLocatorFindStores,
            type: 'GET',
            data: payload,
            dataType: 'json',
            success: function(response) {
                if (locatorSettings.prompt) {
                    _openLocatorModal(response, payload, locatorSettings);
                } else {
                    updateStoresResults(response, payload, locatorSettings);
                }

                publish('storeLocator:updateRequested', {response, payload, locatorSettings});

                progress.hideLoader();
            }
        });
    } else {
        return $.Deferred().resolve({noRequest: true}).promise();
    }
}

export function removeTargetFormData(locatorSettings) {
    let selectedStoreIDInput = getSelectedStoreInput(locatorSettings.targetFormEl),
        selectedStoreDisplayEl = locatorSettings.targetFormEl.querySelector('[data-locator-component="selected-store-display"]');

    delete locatorSettings.locatorReviewed;
    delete locatorSettings.locatorStoreSelected;

    if (selectedStoreIDInput) {
        selectedStoreIDInput.value = '';
    }

    if (selectedStoreDisplayEl) {
        selectedStoreDisplayEl.classList.add('hidden');
    }
}

export function updateTargetFormData(locatorSettings, storeData = {}, displaySelection) {
    let selectedStoreIDInput = getSelectedStoreInput(locatorSettings.targetFormEl) || createElWithAttrs(locatorSettings.targetFormEl, {'type': 'hidden', 'data-locator-component': 'selected-store-id', 'name': storeIDInputName}, 'input'),
        selectedStoreRemoveEl = locatorSettings.targetFormEl.querySelector('[data-locator-component="selected-store-remove"]');

    selectedStoreIDInput.value = storeData.storeID || selectedStoreIDInput.value;

    locatorSettings.locatorReviewed = true;

    if (selectedStoreIDInput.value) {
        locatorSettings.locatorStoreSelected = true;
    }

    if (displaySelection) {
        displayStoreSelection(locatorSettings, storeData);
    }

    if (selectedStoreRemoveEl) {
        selectedStoreRemoveEl.addEventListener('click', removeTargetFormData.bind(this, locatorSettings));
    }
}

export function displayStoreSelection(locatorSettings, storeData) {
    let selectedStoreDisplayEl = locatorSettings.targetFormEl.querySelector('[data-locator-component="selected-store-display"]');

    if (selectedStoreDisplayEl && storeData.storeInfo) {
        selectedStoreDisplayEl.classList.remove('hidden');

        populateAddressSummary(selectedStoreDisplayEl, storeData.storeInfo);
    }
}

export function getSelectedStoreInput(targetFormEl) {
    return targetFormEl ? targetFormEl.querySelector(`[data-locator-component="selected-store-id"], [name="${storeIDInputName}"]`) : null;
}


function _setupLocator(locatorSettings = {}, locatorEl) {
    let hasCustomSettings = locatorEl.getAttribute('data-locator'),
        locatorElIsForm = locatorEl.tagName === 'FORM';

    locatorSettings = objectAssign({}, locatorSettings, hasCustomSettings ? JSON.parse(hasCustomSettings) : {});

    locatorSettings.locatorEl = locatorEl;
    locatorSettings.targetFormEl = locatorSettings.targetFormEl || (locatorSettings.targetFormSelector ? document.querySelector(locatorSettings.targetFormSelector) : (locatorElIsForm ? locatorEl : null));

    //If locator is configured as a prompt, display and configure only after an event happens.
    if (locatorSettings.prompt) {
        locatorSettings.locatorEl.addEventListener('submit', function(evt) {
            //Set this since the flag was removed when opening the modal.
            locatorSettings.prompt = true;

            evt.preventDefault();

            if (_shouldRequestSearch(locatorSettings)) {
                requestStoreSearch('', {}, locatorSettings);
            } else {
                _submitTargetForm(locatorSettings);
            }
        });
    } else {
        //Otherwise just setup the current locator.
        _renderMap({}, {}, locatorSettings);
        _setUIElements(locatorSettings);

        if (locatorSettings.locatorForm) {
            _setupEventListeners(locatorSettings);
        }
    }

    locatorEl.removeAttribute('data-locator');

    // Override for Denali
    if (window.SFRA.Denali) {
        let boutiqueSelect = locatorEl.querySelector('[data-denali-boutique-select]'),
            availabilityCTA = locatorEl.querySelector('[data-denali-boutique-action]');

        if (boutiqueSelect && availabilityCTA) {
            boutiqueSelect.addEventListener('change', function() {
                [].forEach.call(locatorEl.querySelectorAll('[data-denali-boutique-list-item]'), function(currentItem) {
                    currentItem.classList.add('hidden');
                });

                if (this.value) {
                    availabilityCTA.classList.remove('disabled');
                } else {
                    availabilityCTA.classList.add('disabled');
                }
            });

            availabilityCTA.addEventListener('click', function() {
                let storeId = boutiqueSelect.value,
                    storeListItem = document.getElementById(storeId);

                if (storeListItem) {
                    storeListItem.classList.remove('hidden');
                }
            });
        }
    }

    return locatorSettings;
}

/**
 * Customized google map marker icon with svg format
 */
function _getMarkerIcon(locatorSettings) {
    return {
        path: 'M13.5,30.1460153 L16.8554555,25.5 L20.0024287,25.5 C23.039087,25.5 25.5,' +
            '23.0388955 25.5,20.0024287 L25.5,5.99757128 C25.5,2.96091298 23.0388955,0.5 ' +
            '20.0024287,0.5 L5.99757128,0.5 C2.96091298,0.5 0.5,2.96110446 0.5,5.99757128 ' +
            'L0.5,20.0024287 C0.5,23.039087 2.96110446,25.5 5.99757128,25.5 L10.1445445,' +
            '25.5 L13.5,30.1460153 Z',
        fillColor: '#0070d2',
        fillOpacity: 1,
        scale: 1.1,
        strokeColor: 'white',
        strokeWeight: 1,
        anchor: new locatorSettings.mapsAPI.Point(13, 30),
        labelOrigin: new locatorSettings.mapsAPI.Point(12, 12)
    };
}

function _toggleNoResults(show) {
    let noResultsEl = document.querySelector('[data-locator-component="no-results"]');

    if (show) {
        noResultsEl.classList.remove('hidden');
    } else {
        noResultsEl.classList.add('hidden');
    }
}

function _toggleNoAPI(show) {
    let noAPIEl = document.querySelector('[data-locator-component="no-api"]');

    if (show) {
        noAPIEl.classList.remove('hidden');
    } else {
        noAPIEl.classList.add('hidden');
    }
}

/**
 * Uses google maps api to render a map
 */
function _renderMap(response = {}, payload = {}, locatorSettings) {
    let mapTargetEl = locatorSettings.locatorEl.querySelector('[data-locator-component="map"]');

    if (!mapTargetEl) {
        return false;
    }

    if (mapTargetEl.hasAttribute('data-has-google-api')) {
        let mapLocations = JSON.parse(response && response.locations ? response.locations : mapTargetEl.getAttribute('data-locations')),
            infowindow = new locatorSettings.mapsAPI.InfoWindow(),
            latlng = new locatorSettings.mapsAPI.LatLng(payload.lat || INITIAL_LAT, payload.long || INITIAL_LNG),
            mapOptions = {
                zoom: payload.lat ? 12 : 4,
                center: latlng,
                streetViewControl: false
            },
            mapObject = new locatorSettings.mapsAPI.Map(mapTargetEl, mapOptions),
            mapBounds = new locatorSettings.mapsAPI.LatLngBounds();

        Object.keys(mapLocations).forEach(function(key) {
            let locationItem = mapLocations[key],
                locationLabel = parseInt(key, 10) + 1,
                storeLocation = new locatorSettings.mapsAPI.LatLng(locationItem.latitude, locationItem.longitude),
                markerObject = new locatorSettings.mapsAPI.Marker({
                    position: storeLocation,
                    map: mapObject,
                    title: locationItem.name,
                    icon: _getMarkerIcon(locatorSettings),
                    label: { text: locationLabel.toString(), color: 'white', fontSize: '16px' }
                });

            markerObject.addListener('click', function() {
                infowindow.setOptions({
                    content: locationItem.infoWindowHtml
                });

                infowindow.open(mapObject, markerObject);
            });

            // Create a minimum bound based on a set of storeLocations
            mapBounds.extend(markerObject.position);
        });

        // Fit the all the store marks in the center of a minimum bounds when any store has been found.
        if (mapLocations && mapLocations.length) {
            mapObject.fitBounds(mapBounds);
        }
    } else {
        _toggleNoAPI(true);
    }
}

/**
 * Search for stores with new search terms
 * @param {HTMLElement} element - the target html element
 * @returns {boolean} false to prevent default event
 */
function _storeSearchHandler(locatorSettings, evt) {
    let payload = getQueryStringParams($(locatorSettings.locatorForm).find(':input').serialize());

    if (evt) {
        evt.preventDefault();
    }

    if (payload.postalCode || payload.locality) {
        let geocoder = new locatorSettings.mapsAPI.Geocoder(),
            searchObject = {
                address: payload.postalCode || payload.locality,
                componentRestrictions: {}
            };

        progress.showLoader();

        if (payload.locality) {
            searchObject.componentRestrictions.locality = payload.locality;
        }

        if (payload.country) {
            searchObject.componentRestrictions.country = payload.country;
        }

        //Support for determining the location from the input address.
        geocoder.geocode(searchObject, function(results) {
            //Only attempt to access the lat/lang properties if there are any matching results.
            if (results.length) {
                let resultsLocation = results[0].geometry.location;

                delete payload.postalCode;

                payload.lat = resultsLocation.lat();
                payload.long = resultsLocation.lng();
            }

            requestStoreSearch(locatorSettings.locatorForm.getAttribute('action'), payload, locatorSettings);
        });
    }

    return false;
}

function _openLocatorModal(response, payload, locatorSettings) {
    let promptURL = typeof locatorSettings.prompt === 'string' ? locatorSettings.prompt : window.SFRA.Urls.storeLocatorModal;

    //Remove these properties once the modal is open to avoid re-opening the same modal results.
    delete locatorSettings.prompt;

    loadGoogleMapsAPI(APIConfig).then(function(mapsAPI) {
        let dialogSettings;

        locatorSettings.mapsAPI = mapsAPI;

        dialogSettings = {
            options: {
                modalID: 'storeLocator',
                modalClass: 'modal--flex modal--store-locator modal--small-full',
                overlayOthers: true,
                keepAlive: false,
                afterCloseCallback: _storeSelectionClosed.bind(this, locatorSettings)
            },
            callback: function(modal) {
                let updatedLocatorSettings = _setupLocator(locatorSettings, modal);

                publish('storeLocator:updateRendered', {response, payload, locatorSettings: updatedLocatorSettings});
            }
        };

        if (response.storesResultsHtml) {
            dialogSettings.html = response.storesResultsHtml;
        } else {
            dialogSettings.url = appendParamsToURL(promptURL, payload);
            dialogSettings.type = 'html';
        }

        dialog.open(dialogSettings);
    });
}

function _getSelectedStoreData(locatorSettings, evt) {
    let selectedStore = locatorSettings.locatorEl.querySelector('[data-locator-component="result-list"] :checked');

    return selectedStore ? {
        storeID: selectedStore.value,
        searchRadius: locatorSettings.radiusEl.value,
        searchPostalCode: locatorSettings.searchEl.value,
        storeInfo: JSON.parse(selectedStore.getAttribute('data-store-info')),
        event: evt
    } : {};
}

function _detectLocationHandler(locatorSettings) {
    progress.showLoader();

    if (!navigator.geolocation) {
        progress.hideLoader();

        return;
    }

    navigator.geolocation.getCurrentPosition(function(position) {
        let locatorForm = locatorSettings.locatorForm,
            searchEl = locatorSettings.searchEl,
            urlParams = {
                radius: locatorSettings.radiusEl.value,
                lat: position.coords.latitude,
                long: position.coords.longitude
            };

        searchEl.value = `${position.coords.latitude}, ${position.coords.longitude}`;

        requestStoreSearch(locatorForm.getAttribute('action'), urlParams, locatorSettings);
    });
}

/**
 * Handle clicking on the "Cancel" option when presented with a store list.
 * @param  {[type]} evt [description]
 * @return {[type]}     [description]
 */
function _ignoreSelectionHandler(locatorSettings, evt) {
    evt.preventDefault();

    _targetFormHandler(locatorSettings);

    publish('storeLocator:ignored', {locatorSettings});

    publish('storeLocator:reviewed', {locatorSettings});
}

function _confirmSelectionHandler(locatorSettings, evt) {
    let storeData = _getSelectedStoreData(locatorSettings, evt);

    evt.preventDefault();

    _targetFormHandler(locatorSettings, storeData);

    publish('storeLocator:confirmed', {locatorSettings, storeData});

    publish('storeLocator:reviewed', {locatorSettings, storeData});
}

function _storeSelectionHandler(locatorSettings, evt) {
    let storeData = _getSelectedStoreData(locatorSettings, evt);

    if (locatorSettings.confirmSelectionAction) {
        locatorSettings.confirmSelectionAction.disabled = !evt;
    }

    publish('storeLocator:selected', {locatorSettings, storeData});
}

/**
 * Fires an event when the store selection modal is closed.
 */
function _storeSelectionClosed(locatorSettings) {
    publish('storeLocator:closed', {locatorSettings});
}

function _targetFormHandler(locatorSettings, storeData) {
    if (locatorSettings.targetFormEl) {
        updateTargetFormData(locatorSettings, storeData, true);

        _submitTargetForm(locatorSettings);
    }
}

/**
 * Submits the targetForm. Process the form through module.ajax-form if the `ajaxForm` option is provided.
 */
function _submitTargetForm(locatorSettings) {
    if (locatorSettings.submitOnConfirm) {
        if (locatorSettings.ajaxForm) {
            processAjaxForm(locatorSettings.targetFormEl);
        } else {
            //TODO: Some browsers do not allow to fire certain default events, such as a form's "submit", through JS.
            //Dispatch a custom "storeSelected" for now.
            dispatchEvent(locatorSettings.targetFormEl, 'storeSelected');
        }
    }
}

/**
 * Listens for changes on the store results list. i.e. when a store is clicked the map changes, etc.
 */
function _storeSelectionListener(locatorSettings) {
    addEventListenerList('[data-locator-component="store-info"]', 'change', _storeSelectionHandler.bind(this, locatorSettings));
}

function _setUIElements(locatorSettings) {
    locatorSettings.locatorForm = locatorSettings.locatorEl.querySelector('[data-locator-component="form"]');

    locatorSettings.searchEl = locatorSettings.locatorEl.querySelector('[data-locator-component="search-field"]')
    locatorSettings.submitEl = locatorSettings.locatorEl.querySelector('[data-locator-component="submit"]');
    locatorSettings.radiusEl = locatorSettings.locatorEl.querySelector('[data-locator-component="radius-field"]');
    locatorSettings.localityEl = locatorSettings.locatorEl.querySelector('[data-locator-component="locality-field"]');
    locatorSettings.detectLocationEl = locatorSettings.locatorEl.querySelector('[data-locator-component="detect-location"]');

    locatorSettings.confirmSelectionAction = locatorSettings.locatorEl.querySelector('[data-locator-component="confirm-selection"]');
    locatorSettings.ignoreSelectionAction = locatorSettings.locatorEl.querySelector('[data-locator-component="ignore-selection"]');
}

function _setupEventListeners(locatorSettings) {
    // clicking on detect location.
    if (locatorSettings.detectLocationEl) {
        locatorSettings.detectLocationEl.addEventListener('click', _detectLocationHandler.bind(locatorSettings.detectLocationEl, locatorSettings));
    }

    if (locatorSettings.radiusEl) {
        locatorSettings.radiusEl.addEventListener('change', _storeSearchHandler.bind(locatorSettings.radiusEl, locatorSettings));
    }

    if (locatorSettings.localityEl) {
        locatorSettings.localityEl.addEventListener('change', _storeSearchHandler.bind(locatorSettings.localityEl, locatorSettings));
    }

    if (locatorSettings.locatorForm.tagName === 'FORM') {
        locatorSettings.locatorForm.addEventListener('submit', _storeSearchHandler.bind(locatorSettings.locatorForm, locatorSettings));
    } else {
        locatorSettings.submitEl.addEventListener('click', _storeSearchHandler.bind(locatorSettings.submitEl, locatorSettings));
    }

    _storeSelectionListener(locatorSettings);

    if (locatorSettings.confirmSelectionAction) {
        locatorSettings.confirmSelectionAction.addEventListener('click', _confirmSelectionHandler.bind(locatorSettings.confirmSelectionAction, locatorSettings));
    }

    if (locatorSettings.ignoreSelectionAction) {
        locatorSettings.ignoreSelectionAction.addEventListener('click', _ignoreSelectionHandler.bind(locatorSettings.ignoreSelectionAction, locatorSettings));
    }
}

/**
 * Checks if the current `locatorSettings.targetFormEl` already has selected a store,
 * by checking if it can find a `[data-locator-component="selected-store-id"]` with a value in it.
 */
function _shouldRequestSearch(locatorSettings) {
    let selectedStoreIDInput = getSelectedStoreInput(locatorSettings.targetFormEl);

    return !selectedStoreIDInput || !selectedStoreIDInput.value || locatorSettings.allowSearchAfterSelection;
}
